import { DocumentGatewayService } from 'src/app/core/service/document-gateway.service';
import { Component } from '@angular/core';
import { DocumentViewerModel } from './model/document-viewer';
import { DocumentService } from 'src/app/core/service/document.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { DomSanitizer } from '@angular/platform-browser';
import { XmlService } from 'src/app/core/service/xml.service';
import { PDFViewerTool } from '@progress/kendo-angular-pdfviewer';
import { Base64 } from 'js-base64';

@Component({
  standalone: false,
  selector: 'app-document-viewer',
  templateUrl: './document-viewer.component.html',
  styleUrls: ['./document-viewer.component.scss']
})
export class DocumentViewerComponent
{
  public documentViewerDialogOpened = false;
  public isSupportedDocumentType = false;
  public isImage = false;
  public isPdf = false;
  public isXml = false;
  public xslContent: Document = null;
  public loadingDocument = false;
  public model: DocumentViewerModel;
  public saveDialogOpened = false;
  public selectedDocumentName: string;
  public windowState = 'default';
  public zoomImage: number;
  public pdfViewerTools: PDFViewerTool[] = [
    /*Disable search for because it a bit slow with pdf and multiple pages
    'search',*/
    'selection',
    'spacer',
    'zoom',
    'zoomInOut',
    'spacer',
    'download',
    'print',
    'pager',
  ];

  constructor(
    private documentService: DocumentService,
    private messageHandlerService: MessageHandlerService,
    private sanitizer: DomSanitizer,
    private xmlService: XmlService,
    private documentGatewayService: DocumentGatewayService
  ) { }

  public execute(model: DocumentViewerModel): void
  {
    this.zoomImage = 75;
    this.model = model;
    this.buildForm();
  }

  public closeDialog(): void
  {
    this.documentViewerDialogOpened = false;
    document.body.style.overflow = '';
  }

  /**
   * Open the document viewer page
   */
  public openDocument(): void
  {
    this.documentService.retrieveDocument(this.model.url).subscribe({
      next: (data) =>
      {
        // Initialization
        this.loadingDocument = false;
        this.isSupportedDocumentType = true;
        this.isImage = false;
        this.isPdf = false;
        this.isXml = false;

        switch (this.model.mediaType)
        {
          case 'image/bmp':
          case 'image/png':
          case 'image/jpeg':
          case 'image/jpg':
          case 'image/jpe':
            this.isImage = true;
            this.model.image = `data:${this.model.mediaType};base64,${data.content}`;
            // Map all the different jpg extensions to image/jpg
            if (this.model.mediaType.includes('image/jp'))
            {
              this.model.mediaType = 'image/jpg';
            }
            break;

          case 'application/pdf':
            this.isPdf = true;
            this.model.content = `data:${this.model.mediaType};base64,${data.content}`;
            break;

          case 'text/plain':
            this.model.content = Base64.decode(data.content).replace(/(\r\n|\n|\r)/g, '<br />');
            break;

          case 'text/xml':
            this.isXml = true;
            this.model.isCDA = data.isCDA;

            // If this is a CDA, xslt will be applied to xml
            if (data.isCDA)
            {
              this.windowState = 'maximized';
              if (this.xslContent === null)
              {
                // Get XSL stylesheet
                const xmlRequest = new XMLHttpRequest();
                xmlRequest.open('GET', 'assets/files/ccda.xsl', false);
                xmlRequest.overrideMimeType('application/xml');
                xmlRequest.send(null);

                const parser = new DOMParser();
                this.xslContent = parser.parseFromString(xmlRequest.responseText, 'application/xml');
                this.setModelContentFromXslt(data.content);
              }
              else
              {
                this.setModelContentFromXslt(data.content);
              }
            }
            else
            {
              this.model.content = Base64.decode(data.content);
            }
            break;

          default:
            this.isSupportedDocumentType = false;
            break;
        }
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
        this.loadingDocument = false;
        this.closeDialog();
      }
    });
  }

  /**
   * Show the save button for gateways documents only.
   */
  public isSaveVisible(): boolean
  {
    return this.model && this.model.url.includes('/gateway');
  }

  public onSave(): void
  {
    this.selectedDocumentName = this.model.name;
    this.documentViewerDialogOpened = false;
    this.saveDialogOpened = true;
  }

  public saveDocumentReference(): void
  {
    this.documentGatewayService.saveDocumentReference(this.model.url).subscribe({
      next: () =>
      {
        // Show a success
        this.messageHandlerService.displaySuccess('DOCUMENT-ADDED-SUCCESFULLY');
      },
      error: error =>
      {
        this.messageHandlerService.displayError(error);
      },
      complete: () =>
      {
        this.saveDialogOpened = false;
      }
    });
  }

  private buildForm(): void
  {
    // Reset content and image
    this.model.content = '';
    this.model.image = '';
    this.windowState = 'default';
    this.loadingDocument = true;
    this.documentViewerDialogOpened = true;

    this.openDocument();
    document.body.style.overflow = 'hidden';
  }

  /**
   * This method set the model content based on the xslt for the C-CDA rendering.
   *
   * @param content XML content.
   */
  private setModelContentFromXslt(content: string): void
  {
    // Sanitize the html
    this.model.content = this.sanitizer.bypassSecurityTrustHtml(
      this.xmlService.getTransformedHtml(this.xslContent, Base64.decode(content)));
  }
}
